/**
 * 🧪 TEST DATA INITIALIZATION PAGE
 * Admin tool to initialize test data for verification and check-in/checkout system
 * This should only be available in development/staging environments
 */

import React, { useState } from 'react';
import { getApiBaseUrl } from '@/config/environment';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stepper,
  Step,
  StepLabel,
  StepContent,
} from '@mui/material';
import {
  Science,
  PlayArrow,
  CheckCircle,
  Warning,
  Code,
  Storage,
  CloudUpload,
  Refresh,
} from '@mui/icons-material';
import toast from 'react-hot-toast';

// API_BASE is now dynamic

export const TestDataInitializationPage: React.FC = () => {
  const [bookingId, setBookingId] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [activeStep, setActiveStep] = useState(0);

  const handleInitialize = async () => {
    if (!bookingId.trim()) {
      toast.error('Please enter a booking ID');
      return;
    }

    setLoading(true);
    setResult(null);
    setActiveStep(0);

    try {
      // Step 1: Validate booking exists
      setActiveStep(1);
      await new Promise(resolve => setTimeout(resolve, 500));

      // Step 2: Initialize verification data
      setActiveStep(2);
      const response = await fetch(`${getApiBaseUrl()}/api/v1/admin/test-data/initialize-verification`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ bookingId: bookingId.trim() })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || 'Failed to initialize test data');
      }

      const data = await response.json();

      // Step 3: Verify data
      setActiveStep(3);
      await new Promise(resolve => setTimeout(resolve, 500));

      // Step 4: Complete
      setActiveStep(4);
      setResult(data);
      toast.success('Test data initialized successfully!');

    } catch (error) {
      console.error('Error initializing test data:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to initialize test data');
      setResult({
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      });
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    'Enter Booking ID',
    'Validate Booking',
    'Initialize Data',
    'Verify Setup',
    'Complete'
  ];

  return (
    <Box sx={{ p: 3 }}>
      {/* Page Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Science color="primary" sx={{ fontSize: 32 }} />
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Test Data Initialization
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          Initialize verification code and check-in/checkout test data for development and testing
        </Typography>
      </Box>

      {/* Warning Alert */}
      <Alert severity="warning" icon={<Warning />} sx={{ mb: 3 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
          Development/Staging Only
        </Typography>
        <Typography variant="caption">
          This tool should only be used in development or staging environments.
          It will modify booking data and create test verification codes and check-in/checkout records.
        </Typography>
      </Alert>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
        {/* Left Column - Input and Actions */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Initialize Test Data
          </Typography>

          <TextField
            fullWidth
            label="Booking ID"
            placeholder="Enter booking UUID"
            value={bookingId}
            onChange={(e) => setBookingId(e.target.value)}
            disabled={loading}
            sx={{ mb: 3 }}
            helperText="Enter the UUID of an existing booking to initialize test data"
          />

          <Button
            fullWidth
            variant="contained"
            size="large"
            startIcon={loading ? <CircularProgress size={20} /> : <PlayArrow />}
            onClick={handleInitialize}
            disabled={loading || !bookingId.trim()}
          >
            {loading ? 'Initializing...' : 'Initialize Test Data'}
          </Button>

          {/* Progress Stepper */}
          {loading && (
            <Box sx={{ mt: 3 }}>
              <Stepper activeStep={activeStep} orientation="vertical">
                {steps.map((label, index) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                    <StepContent>
                      <Typography variant="caption" color="text.secondary">
                        {index === 1 && 'Checking if booking exists...'}
                        {index === 2 && 'Creating verification code and check-in/checkout data...'}
                        {index === 3 && 'Verifying data was created successfully...'}
                      </Typography>
                    </StepContent>
                  </Step>
                ))}
              </Stepper>
            </Box>
          )}

          {/* Result Display */}
          {result && !loading && (
            <Box sx={{ mt: 3 }}>
              {result.success ? (
                <Alert severity="success" icon={<CheckCircle />}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                    Test Data Initialized Successfully!
                  </Typography>
                  <Typography variant="caption" component="div">
                    Booking ID: {bookingId}
                  </Typography>
                  {result.data?.verificationCode && (
                    <Typography variant="caption" component="div">
                      Verification Code: <strong>{result.data.verificationCode}</strong>
                    </Typography>
                  )}
                </Alert>
              ) : (
                <Alert severity="error">
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                    Initialization Failed
                  </Typography>
                  <Typography variant="caption">
                    {result.error || 'Unknown error occurred'}
                  </Typography>
                </Alert>
              )}
            </Box>
          )}
        </Paper>

        {/* Right Column - What Gets Created */}
        <Box>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Storage color="primary" />
                What Gets Created
              </Typography>

              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle color="success" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Verification Code"
                    secondary="6-digit code with verified status"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle color="success" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Check-in Data"
                    secondary="Renter and host check-in photos, mileage, fuel level"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle color="success" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Check-out Data"
                    secondary="Renter and host check-out photos, damage reports"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle color="success" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Dispute Record"
                    secondary="Sample checkout dispute (host reported damage)"
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Code color="primary" />
                Test Data Details
              </Typography>

              <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
                Verification Code:
              </Typography>
              <Chip label="482756" size="small" sx={{ mb: 2, fontFamily: 'monospace' }} />

              <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
                Check-in:
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, fontSize: '0.8rem' }}>
                • Mileage: 45,230 km<br />
                • Fuel: 95%<br />
                • Status: Completed 3 days ago
              </Typography>

              <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
                Check-out:
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, fontSize: '0.8rem' }}>
                • Mileage: 45,680 km (+450 km)<br />
                • Fuel: 90%<br />
                • Dispute: Host reported damage
              </Typography>

              <Divider sx={{ my: 2 }} />

              <Alert severity="info" icon={<CloudUpload />} sx={{ fontSize: '0.75rem' }}>
                <Typography variant="caption">
                  Sample placeholder images will be used for photos. Replace with real Cloudinary URLs for production testing.
                </Typography>
              </Alert>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Instructions Card */}
      <Paper sx={{ p: 3, mt: 3, backgroundColor: 'action.hover' }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          📋 How to Use This Tool
        </Typography>

        <Typography variant="body2" sx={{ mb: 2 }}>
          <strong>Step 1:</strong> Find a booking ID from your database or create a test booking first
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          <strong>Step 2:</strong> Enter the booking ID above and click "Initialize Test Data"
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          <strong>Step 3:</strong> Go to Disputes page, find a dispute for this booking
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          <strong>Step 4:</strong> Open the dispute and you should see "Verification Code" and "Check-in/Check-out" tabs
        </Typography>

        <Divider sx={{ my: 2 }} />

        <Typography variant="caption" color="text.secondary">
          <strong>Note:</strong> If you don't see the tabs, make sure:
          <br />• The booking ID exists in the database
          <br />• The backend endpoint is implemented
          <br />• The dispute is linked to the booking (has booking_id field)
        </Typography>
      </Paper>
    </Box>
  );
};

export default TestDataInitializationPage;
